import { Fragment, useMemo } from 'react';
import { lerMarkdown, type Alinhamento, type Trecho } from './markdown';

function Trechos(props: { readonly trechos: readonly Trecho[] }): JSX.Element {
  return (
    <>
      {props.trechos.map((t, i) =>
        t.tipo === 'negrito' ? (
          <strong key={i} className="font-semibold text-conteudo-primario">
            {t.texto}
          </strong>
        ) : t.tipo === 'italico' ? (
          <em key={i}>{t.texto}</em>
        ) : t.tipo === 'codigo' ? (
          <code
            key={i}
            className="rounded bg-superficie-vidro px-1 py-0.5 font-mono text-[0.85em] text-conteudo-primario"
          >
            {t.texto}
          </code>
        ) : (
          <Fragment key={i}>{t.texto}</Fragment>
        ),
      )}
    </>
  );
}

const ALINHAMENTO: Readonly<Record<Alinhamento, string>> = {
  esquerda: 'text-left',
  // Número não quebra: "R$" numa linha e o valor na outra confunde a leitura.
  direita: 'whitespace-nowrap text-right tabular-nums',
  centro: 'text-center',
};

/**
 * A resposta da BECA, montada a partir do Markdown dela (ver `markdown.ts`).
 * Nada aqui vira HTML cru: o texto entra sempre como texto.
 */
export function TextoDaBeca(props: { readonly texto: string }): JSX.Element {
  const blocos = useMemo(() => lerMarkdown(props.texto), [props.texto]);

  return (
    <div className="flex flex-col gap-3 text-sm leading-relaxed text-conteudo-primario">
      {blocos.map((b, i) => {
        if (b.tipo === 'paragrafo') {
          return (
            <p key={i}>
              {b.linhas.map((linha, j) => (
                <Fragment key={j}>
                  {j > 0 && <br />}
                  <Trechos trechos={linha} />
                </Fragment>
              ))}
            </p>
          );
        }
        if (b.tipo === 'titulo') {
          return (
            <p key={i} className="font-semibold text-conteudo-primario">
              <Trechos trechos={b.trechos} />
            </p>
          );
        }
        if (b.tipo === 'lista') {
          const Lista = b.ordenada ? 'ol' : 'ul';
          return (
            <Lista
              key={i}
              className={`flex flex-col gap-1 pl-5 ${b.ordenada ? 'list-decimal' : 'list-disc'} marker:text-conteudo-sutil`}
            >
              {b.itens.map((item, j) => (
                <li key={j} className="pl-1">
                  <Trechos trechos={item} />
                </li>
              ))}
            </Lista>
          );
        }
        return (
          <div key={i} className="overflow-x-auto rounded-card border border-borda">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-superficie-vidro/70">
                <tr>
                  {b.cabecalho.map((c, j) => (
                    <th
                      key={j}
                      scope="col"
                      className={`whitespace-nowrap px-3 py-2 text-xs font-semibold uppercase tracking-wide text-conteudo-secundario ${ALINHAMENTO[b.alinhamentos[j] ?? 'esquerda']}`}
                    >
                      <Trechos trechos={c} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.linhas.map((linha, j) => (
                  <tr key={j} className="border-t border-borda">
                    {linha.map((c, k) => (
                      <td
                        key={k}
                        className={`px-3 py-2 align-top ${ALINHAMENTO[b.alinhamentos[k] ?? 'esquerda']}`}
                      >
                        <Trechos trechos={c} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
